import React, { useState, useCallback, useEffect, useRef } from 'react';
import EpubViewer from './EpubViewer';
import PdfViewer from './PdfViewer';
import FloatingAIButton from './FloatingAIButton';
import './UnifiedReaderCore.css';

const EPUB_MIME = 'application/epub+zip';
const PDF_MIME = 'application/pdf';

/**
 * Conteneur : octets du fichier (`fileData`, ArrayBuffer déjà téléchargé avec
 * l'authentification) + type MIME, état de sélection, sous-lecteurs EPUB/PDF + FAB IA.
 */
export default function UnifiedReaderCore({
  fileData,
  mimeType,
  onAskAI,
  className,
}) {
  const [selectedText, setSelectedText] = useState('');
  const [selectionCoords, setSelectionCoords] = useState({ x: 0, y: 0 });
  const [pageContext, setPageContext] = useState(null);
  const fabRef = useRef(null);
  const containerRef = useRef(null);

  const onSelectionChange = useCallback((payload) => {
    if (!payload?.text) {
      setSelectedText('');
      setPageContext(null);
      return;
    }
    setSelectedText(payload.text);
    setSelectionCoords({ x: payload.x, y: payload.y });
    setPageContext(payload.pageContext ?? null);
  }, []);

  useEffect(() => {
    const onPointerDown = (e) => {
      const t = e.target;
      if (fabRef.current?.contains(t)) return;
      if (containerRef.current?.contains(t)) return;
      setSelectedText('');
      setPageContext(null);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, []);

  const handleAsk = useCallback(() => {
    if (!selectedText.trim()) return;
    onAskAI?.(selectedText, pageContext);
    setSelectedText('');
    setPageContext(null);
  }, [selectedText, pageContext, onAskAI]);

  let body = null;
  if (mimeType === EPUB_MIME) {
    body = (
      <EpubViewer
        fileData={fileData}
        onSelectionChange={onSelectionChange}
      />
    );
  } else if (mimeType === PDF_MIME) {
    body = (
      <PdfViewer
        fileData={fileData}
        onSelectionChange={onSelectionChange}
      />
    );
  } else {
    body = (
      <div className="unified-reader-core-unsupported">
        Format non pris en charge : {mimeType || 'inconnu'}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`unified-reader-core ${className || ''}`}
    >
      <div className="unified-reader-core-inner">{body}</div>
      <FloatingAIButton
        buttonRef={fabRef}
        x={selectionCoords.x}
        y={selectionCoords.y}
        visible={Boolean(selectedText.trim())}
        onClick={handleAsk}
      />
    </div>
  );
}

export { EPUB_MIME, PDF_MIME };
